import type { FC } from "react";
import EventLink from "@/components/Link/EventLink/EventLink";
import { ElementType } from "@/constants/tagular/main";
import useTranslations from "@/i18n/useTranslations";
import { useLocale } from "next-intl";
import { withLocalePrefix } from "@/i18n/withLocalePrefix";

type CategoryLink = {
  label: string;
  url: string;
};

interface TrendingSectionProps {
  categories: CategoryLink[];
  sectionHeader?: string;
  mobileCategoryLimit?: number;
  location?: string;
}

const DEFAULT_MOBILE_LIMIT = 6;

const TrendingSection: FC<TrendingSectionProps> = ({
  categories,
  sectionHeader,
  mobileCategoryLimit = DEFAULT_MOBILE_LIMIT,
  location = "embedded-search",
}) => {
  const t = useTranslations("Search");
  const locale = useLocale();
  const header = sectionHeader ?? t("trendingOnEdx");
  const finiteLimit = Number.isFinite(mobileCategoryLimit)
    ? mobileCategoryLimit
    : DEFAULT_MOBILE_LIMIT;
  const safeLimit = Math.max(
    0,
    Math.min(Math.round(finiteLimit), categories.length),
  );

  if (categories.length === 0) return null;

  return (
    <div className="pb-2">
      <span className="elm-p2 text-gray-800 font-bold uppercase mb-4 block">
        {header}
      </span>
      <ul className="list-none !p-0 my-0 flex flex-wrap gap-2 pb-2">
        {categories.map((category, index) => (
          <li
            key={`${category.label}-${category.url}`}
            className={`m-0${index >= safeLimit ? " hidden md:block" : ""}`}
          >
            <EventLink
              href={withLocalePrefix(category.url, locale)}
              element_type={ElementType.Entry}
              html_id="trending-on-edx"
              location={location}
              text={category.label}
              stickyElementView
              position={(index + 1).toString()}
              class_name="inline-block rounded-[0.25rem] bg-putty-400 px-2 py-1 text-sm font-normal text-primary no-underline hover:bg-primary hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TrendingSection;
